import React, { useContext, useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CartContext } from "../context/CartContext";
import { OrderContext } from "../context/OrderContext";
import { useCustomer } from "../context/CustomerContext";
import { useLoyaltySettings } from "../context/LoyaltySettingsContext";
import { getLoyaltyQuote } from "../api/loyaltyApi";
import { getProductDisplayName } from "../utils/productDisplayName";
import {
  buildLoyaltyOrderFields,
  formatKopecks,
  kopecksToHryvniaNumber,
  normalizeLoyaltyQuote,
  LOYALTY_MAINTENANCE_MESSAGE,
} from "../utils/loyalty";
import {
  createMonobankPayment,
  payWithGooglePay,
} from "../services/paymentService";
import {
  clearMonobankPaymentDraft,
  getOrCreateMonobankPaymentDraft,
  getMonobankPendingOrder,
  saveMonobankPaymentDraft,
} from "../utils/monobankPaymentDraft";
import Notification from "./Notification";
import GooglePayButton from "./GooglePayButton";
import {
  FaBoxOpen,
  FaUser,
  FaMoneyBillWave,
  FaTruck,
  FaArrowLeft,
  FaCheckCircle,
  FaMinus,
  FaPlus,
} from "react-icons/fa";
import styles from "./OrderSummary.module.css";
import {
  getDeliveryMethodLabel,
  isBranchDelivery,
  isKyivCourierDelivery,
  isNovaPoshtaCourierDelivery,
} from "../api/checkoutDeliverySettingsApi";
import {
  DEFAULT_NOVA_POSHTA_SETTINGS,
  formatUah,
  getNovaPoshtaFreeDeliveryThreshold,
} from "../api/siteSettingsApi";
import { useDeliverySettings } from "../context/DeliverySettingsContext";
import {
  PAYMENT_CHANNELS,
  PAYMENT_METHODS,
  isOnlineCardPayment,
} from "../config/paymentMethods";
import {
  formatNovaPostCityLabel,
  formatNovaPostDivisionLabel,
} from "../utils/novaPoshtaDelivery";

const orderPriceFormatter = new Intl.NumberFormat("uk-UA", {
  maximumFractionDigits: 2,
});

const formatOrderPrice = (value) =>
  `${orderPriceFormatter.format(Number(value) || 0)} ₴`;
const OrderSummary = () => {
  const { settings: programSettings } = useLoyaltySettings();
  const { state } = useLocation();
  const { settings: deliverySettings } = useDeliverySettings();
  const {
    cartItems = [],
    customerDetails = {},
    totalAmount = 0,
    loyaltyQuote: initialLoyaltyQuote = null,
    bonusKopecksToRedeem: initialLoyaltyBonusKopecksToRedeem = 0,
  } = state || {};
  const { clearCart, refreshCartPrices } = useContext(CartContext);
  const { addOrder } = useContext(OrderContext);
  const { customer } = useCustomer();
  const navigate = useNavigate();

  const [showNotification, setShowNotification] = useState(false);
  const [newOrderId, setNewOrderId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("Створюємо ваше замовлення...");
  const [paymentUnavailableMessage, setPaymentUnavailableMessage] = useState(
    "Онлайн-оплата тимчасово недоступна"
  );
  const [showPaymentUnavailableModal, setShowPaymentUnavailableModal] =
    useState(false);
  const [loyaltyQuote, setLoyaltyQuote] = useState(() =>
    initialLoyaltyQuote ? normalizeLoyaltyQuote(initialLoyaltyQuote) : null,
  );
  const [orderError, setOrderError] = useState("");

  const infoSectionRef = useRef(null);
  const isConfirmingRef = useRef(false);
  const [infoHeight, setInfoHeight] = useState("auto");

  const usesOnlineCardPayment = isOnlineCardPayment(
    customerDetails.paymentMethod
  );
  useEffect(() => {
    if (!infoSectionRef.current) return undefined;

    const updateInfoHeight = () => {
      setInfoHeight(infoSectionRef.current?.offsetHeight || "auto");
    };

    updateInfoHeight();
    const observer = new ResizeObserver(updateInfoHeight);
    observer.observe(infoSectionRef.current);

    return () => observer.disconnect();
  }, []);


  const formattedCartItems = cartItems.map((item) => ({
    productId: item.productId || null,
    productCode: item.productCode,
    name: item.name,
    manufacturer: item.manufacturer,
    weight: item.weight || "",
    price: item.discountPrice ?? item.price,
    regularPrice: item.price,
    discountPrice: item.discountPrice ?? null,
    quantity: item.quantity,
    image: item.image,
    averageCostPrice: item.averageCostPrice ? Number(item.averageCostPrice) : 0,
  }));

  const getDeliveryCost = () => {
    const method = customerDetails?.deliveryMethod;
    const thresholdAmount =
      loyaltyQuote?.itemsSubtotalKopecks !== null &&
      loyaltyQuote?.itemsSubtotalKopecks !== undefined
        ? kopecksToHryvniaNumber(loyaltyQuote.itemsSubtotalKopecks)
        : totalAmount;
    const threshold = getNovaPoshtaFreeDeliveryThreshold(
      method,
      deliverySettings.novaPoshta
    );
    if (threshold) {
      const remaining = Math.max(0, threshold - thresholdAmount);
      return remaining === 0
        ? "безкоштовно (за рахунок продавця)"
        : `за тарифами оператора (додайте ще ${formatUah(remaining)} для безкоштовної доставки)`;
    }
    if (
      isKyivCourierDelivery(method) &&
      thresholdAmount >= DEFAULT_NOVA_POSHTA_SETTINGS.courierFreeDeliveryThreshold
    ) {
      return "безкоштовно (за рахунок продавця)";
    }
    return "за тарифами оператора";
  };

  const buildOrderPayload = (quote = loyaltyQuote) => {
    const loyaltyFields = buildLoyaltyOrderFields({
      customer,
      quote,
      fallbackBonusKopecks: initialLoyaltyBonusKopecksToRedeem,
    });
    const authoritativeTotal =
      quote?.payableItemsKopecks !== null &&
      quote?.payableItemsKopecks !== undefined
        ? kopecksToHryvniaNumber(quote.payableItemsKopecks, totalAmount)
        : totalAmount || 0;

    return {
      customerName: customerDetails.name || "Н/Д",
      phone: customerDetails.phone || "Н/Д",
      email: customerDetails.email || "",
      deliveryMethod: customerDetails.deliveryMethod || "Н/Д",
      city:
        formatNovaPostCityLabel(
          customerDetails.novaPoshtaDelivery?.city,
          customerDetails.city,
        ) || "Н/Д",
      branchNumber: formatNovaPostDivisionLabel(
        customerDetails.novaPoshtaDelivery?.division,
        customerDetails.branchNumber,
      ),
      novaPostCityRef: customerDetails.novaPostCityRef || "",
      novaPostBranchRef: customerDetails.novaPostBranchRef || "",
      novaPostBranchNumber: customerDetails.novaPostBranchNumber || "",
      ...(customerDetails.novaPostPointType
        ? { novaPostPointType: customerDetails.novaPostPointType }
        : {}),
      novaPoshtaDelivery: customerDetails.novaPoshtaDelivery || undefined,
      street: customerDetails.street || "",
      buildingNumber: customerDetails.buildingNumber || "",
      postalCode: customerDetails.postalCode || "",
      apartmentNumber: customerDetails.apartmentNumber || "",
      entranceNumber: customerDetails.entranceNumber || "",
      floor: customerDetails.floor || "",
      courierNote: customerDetails.courierNote || "",
      paymentMethod: customerDetails.paymentMethod || "Н/Д",
      totalAmount: authoritativeTotal,
      ...loyaltyFields,
      cartItems: formattedCartItems,
    };
  };

  const refreshOrderQuote = async () => {
    const requestedBonusKopecks = customer
      ? loyaltyQuote?.appliedBonusKopecks ?? initialLoyaltyBonusKopecksToRedeem
      : 0;
    setOrderError("");

    try {
      const nextQuote = await getLoyaltyQuote({
        cartItems,
        requestedBonusKopecks,
      });
      if (!nextQuote.hasAuthoritativeTotals) {
        throw new Error("Backend не повернув підтверджений фінансовий розрахунок.");
      }
      setLoyaltyQuote(nextQuote);
      if (loyaltyQuote && (nextQuote.appliedBonusKopecks !== loyaltyQuote.appliedBonusKopecks || nextQuote.payableItemsKopecks !== loyaltyQuote.payableItemsKopecks)) {
        throw Object.assign(new Error("Умови замовлення змінилися. Суму перераховано. Перевірте її та повторно підтвердьте оплату або оформлення."), { code: "LOYALTY_QUOTE_CHANGED" });
      }
      return nextQuote;
    } catch (quoteError) {
      const message =
        quoteError?.response?.data?.message ||
        quoteError?.message ||
        "Не вдалося оновити суму та бонусний ліміт.";
      setOrderError(message);
      throw quoteError;
    }
  };

  const saveOnlinePaymentDraft = (paymentDraft, payment) =>
    saveMonobankPaymentDraft({
      ...paymentDraft,
      invoiceId: payment.invoiceId || payment.paymentId || "",
      orderId: payment.orderId || "",
      orderNumber: payment.orderNumber || "",
      paymentUrl: payment.paymentUrl || "",
      paymentAppUrl: payment.paymentAppUrl || "",
      providerStatus: payment.providerStatus || "",
      paymentCreatedAt: payment.paymentCreatedAt || "",
      paymentExpiresAt: payment.paymentExpiresAt || "",
    });

  const requestOnlinePayment = async (paymentChannel) => {
    const latestQuote = await refreshOrderQuote();
    const newOrder = buildOrderPayload(latestQuote);
    const paymentDraft = getOrCreateMonobankPaymentDraft(newOrder);
    const pendingOrder = getMonobankPendingOrder();
    const existingOrderId = paymentDraft.orderId || pendingOrder.orderId;
    const existingOrderNumber =
      paymentDraft.orderNumber || pendingOrder.orderNumber;
    const payment = await createMonobankPayment({
      ...newOrder,
      paymentChannel,
      idempotencyKey: paymentDraft.idempotencyKey,
      existingOrderId,
      existingOrderNumber,
    });

    saveOnlinePaymentDraft(paymentDraft, payment);

    return {
      ...payment,
      idempotencyKey: paymentDraft.idempotencyKey,
      orderPayload: newOrder,
    };
  };

  const showOnlinePaymentError = async (error) => {
    if (error?.response?.data?.code === "CART_PRICES_CHANGED") { await refreshCartPrices(); navigate("/cart"); return; }
    const code = error?.response?.data?.code || error?.code;
    if (code?.startsWith("LOYALTY_")) {
      if (error?.response) await refreshOrderQuote().catch(() => undefined);
      setOrderError(error?.response?.data?.message || error.message);
      return;
    }
    if (code === "PAYMENT_ORDER_CANCELED") {
      clearMonobankPaymentDraft();
    } else if (code === "PAYMENT_ATTEMPT_EXPIRED") {
      clearMonobankPaymentDraft({ includePendingOrder: false });
    } else if (
      code === "PAYMENT_UNAVAILABLE" ||
      code === "PAYMENT_ATTEMPT_CLOSED"
    ) {
      clearMonobankPaymentDraft();
    }

    setPaymentUnavailableMessage(
      error?.response?.data?.message ||
        error?.message ||
        "Онлайн-оплата тимчасово недоступна"
    );
    setShowPaymentUnavailableModal(true);
  };

  const handleOnlinePayment = async (paymentChannel) => {
    if (isConfirmingRef.current || isLoading) return;

    isConfirmingRef.current = true;
    setIsLoading(true);
    setLoadingText("Створюємо безпечне посилання для оплати...");

    try {
      const payment = await requestOnlinePayment(paymentChannel);
      if (!payment?.paymentUrl) {
        throw new Error("Не вдалося отримати посилання для онлайн-оплати");
      }

      localStorage.removeItem("checkoutData");
      window.location.assign(payment.paymentUrl);
    } catch (error) {
      await showOnlinePaymentError(error);
    } finally {
      isConfirmingRef.current = false;
      setIsLoading(false);
    }
  };

  const prepareGooglePayment = async () => {
    if (isConfirmingRef.current || isLoading) {
      throw new Error("Інший платіж уже готується.");
    }

    isConfirmingRef.current = true;
    setIsLoading(true);
    setLoadingText("Готуємо замовлення для Google Pay...");

    try {
      return await requestOnlinePayment(PAYMENT_CHANNELS.GOOGLE_PAY);
    } finally {
      isConfirmingRef.current = false;
      setIsLoading(false);
    }
  };

  const completeGooglePayment = (payment, prepared) => {
    const currentDraft = getOrCreateMonobankPaymentDraft(
      prepared?.orderPayload || buildOrderPayload(),
    );
    saveOnlinePaymentDraft(currentDraft, {
      ...payment,
      orderId: payment?.orderId || prepared?.orderId,
      orderNumber: payment?.orderNumber || prepared?.orderNumber,
    });
    localStorage.removeItem("checkoutData");

    if (payment?.tdsUrl) {
      window.location.assign(payment.tdsUrl);
      return;
    }

    navigate(`/payment/processing?orderId=${payment?.orderId || prepared?.orderId}`);
  };

  const handleConfirmOrder = async () => {
    if (usesOnlineCardPayment) {
      await handleOnlinePayment(PAYMENT_CHANNELS.MONOBANK_INVOICE);
      return;
    }

    if (isConfirmingRef.current || isLoading) return;

    isConfirmingRef.current = true;
    setIsLoading(true);
    setLoadingText("Створюємо ваше замовлення...");

    try {
      const latestQuote = await refreshOrderQuote();
      const newOrder = buildOrderPayload(latestQuote);
      const createdOrder = await addOrder(newOrder);
      setNewOrderId(createdOrder.orderNumber);
      setShowNotification(true);
      localStorage.removeItem("checkoutData");
      clearMonobankPaymentDraft();
      clearCart();

      const isRequisitesPayment =
        customerDetails.paymentMethod === PAYMENT_METHODS.BANK_TRANSFER;

      setTimeout(() => {
        if (isRequisitesPayment) {
          navigate("/payment-requisites", {
            state: {
              orderNumber: createdOrder.orderNumber,
              createdAt: createdOrder.createdAt || createdOrder.created_at,
              customerEmail: customerDetails.email || "",
              totalAmount: createdOrder.totalAmount ?? newOrder.totalAmount,
              paymentDetails: createdOrder.paymentDetails || null,
            },
          });
          return;
        }

        navigate("/order-confirmation", {
          state: { orderNumber: createdOrder.orderNumber },
        });
      }, 3000);
    } catch (error) {
      if (error?.response?.data?.code === "CART_PRICES_CHANGED") { await refreshCartPrices(); navigate("/cart"); return; }
      console.error("❌ Помилка створення замовлення:", error.message);
      if (error?.response?.data?.code?.startsWith("LOYALTY_")) await refreshOrderQuote().catch(() => undefined);

      setOrderError(
        error?.response?.data?.message ||
          error?.message ||
          "Сталася помилка під час створення замовлення.",
      );
    } finally {
      isConfirmingRef.current = false;
      setIsLoading(false);
    }
  };

  const formatDeliveryAddress = () => {
    // якщо є готове поле address
    if (customerDetails.address) return customerDetails.address;

    const { street, buildingNumber, entranceNumber } = customerDetails;
    const parts = [];

    if (street) parts.push(`вул. ${street}`);
    if (buildingNumber) parts.push(`${buildingNumber}`);
    if (entranceNumber) parts.push(`під'їзд ${entranceNumber}`);

    if (parts.length === 0) return "Н/Д";
    return parts.join(", ");
  };

  const hasAuthoritativeTotals = Boolean(
    loyaltyQuote?.hasAuthoritativeTotals,
  );
  const productsTotalText = hasAuthoritativeTotals
    ? formatKopecks(loyaltyQuote.productsBeforeDiscountKopecks)
    : formatOrderPrice(totalAmount);
  const payableItemsText = hasAuthoritativeTotals
    ? formatKopecks(loyaltyQuote.payableItemsKopecks)
    : formatOrderPrice(totalAmount);
  const payableItemsAmount = hasAuthoritativeTotals
    ? kopecksToHryvniaNumber(loyaltyQuote.payableItemsKopecks, totalAmount)
    : totalAmount;

  return (
    <div className={styles.orderSummary}>
      {showNotification && (
        <Notification
          message={`Ваше замовлення №${newOrderId} успішно створено!`}
          onClose={() => setShowNotification(false)}
        />
      )}

      {isLoading && (
        <div className={styles.overlay}>
          <div className={styles.loader}></div>
          <p className={styles.loaderText}>{loadingText}</p>
        </div>
      )}

      {showPaymentUnavailableModal && (
        <div
          className={styles.paymentModalOverlay}
          onClick={() => setShowPaymentUnavailableModal(false)}
        >
          <div
            className={styles.paymentModal}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Цей спосіб оплати тимчасово недоступний</h3>
            <p>{paymentUnavailableMessage}</p>
            <p>Будь ласка, поверніться та оберіть інший спосіб оплати:</p>
            <ul>
              <li>Оплата при отриманні</li>
              <li>Оплата за реквізитами</li>
            </ul>
            <button
              className={styles.modalBackButton}
              onClick={() => {
                setShowPaymentUnavailableModal(false);
                navigate("/cart");
              }}
            >
              Обрати інший спосіб оплати
            </button>
          </div>
        </div>
      )}

      <h1 className={styles.title}>
        <FaBoxOpen className={styles.iconTitle} /> Перегляд замовлення
      </h1>
      {orderError && (
        <p className={styles.orderError} role="alert">
          {orderError}
        </p>
      )}

      <div className={styles.layout}>
        {/* === ЛІВА КОЛОНКА === */}
        <div
          className={styles.cartSection}
          style={{
            height: infoHeight !== "auto" ? `${infoHeight}px` : "auto",
          }}
        >
          <div className={styles.cartList}>
            {cartItems.map((item, index) => {
              const regularPrice = Number(item.price) || 0;
              const promotionalPrice = Number(item.discountPrice);
              const hasDiscount =
                Number.isFinite(promotionalPrice) &&
                promotionalPrice > 0 &&
                promotionalPrice < regularPrice;
              const currentPrice = hasDiscount
                ? promotionalPrice
                : regularPrice;
              const lineTotal = currentPrice * item.quantity;
              const oldLineTotal = regularPrice * item.quantity;
              const discountPercent = hasDiscount
                ? Math.round(
                    ((regularPrice - promotionalPrice) / regularPrice) * 100,
                  )
                : 0;
              const displayName = getProductDisplayName(item);
              const manufacturerCountry = [
                item.manufacturer,
                item.countryOfOrigin,
              ]
                .filter(Boolean)
                .join(", ");

              return (
                <article key={index} className={styles.cartItem}>
                  <div className={styles.itemTopRow}>
                    <div className={styles.imageWrapper}>
                      <img
                        src={item.image}
                        alt={item.name}
                        className={styles.image}
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className={styles.cartDetails}>
                      <p className={styles.itemName}>{displayName}</p>
                      <p className={styles.desktopMeta}>
                        <span>Артикул: {item.productCode || "—"}</span>
                        <span
                          className={styles.desktopMetaSeparator}
                          aria-hidden="true"
                        >
                          |
                        </span>
                        <span>Виробник: {item.manufacturer || "—"}</span>
                      </p>
                      <p className={styles.mobileMetaInfo}>
                        {"\u041a\u043e\u0434: "}
                        {item.productCode}
                        {" | \u0412\u0438\u0440\u043e\u0431\u043d\u0438\u043a: "}
                        {manufacturerCountry}
                      </p>

                      <div className={styles.mobileMetaRow}>
                        <span className={styles.mobileQuantity}>
                          {"\u041a\u0456\u043b\u044c\u043a\u0456\u0441\u0442\u044c: "}
                          {item.quantity}
                        </span>
                        <div className={styles.mobileTotalBlock}>
                          {hasDiscount ? (
                            <>
                              <span className={styles.mobileOldPrice}>
                                {oldLineTotal}{" \u20b4"}
                              </span>
                              <strong className={styles.mobileDiscountTotal}>
                                {lineTotal}{" \u20b4"}
                              </strong>
                            </>
                          ) : (
                            <strong className={styles.mobileTotalValue}>
                              {lineTotal}{" \u20b4"}
                            </strong>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className={styles.desktopPriceSummary}>
                      <div className={styles.desktopItemPriceSummary}>
                        {hasDiscount ? (
                          <>
                            <div className={styles.desktopOldPriceRow}>
                              <span className={styles.desktopOldPrice}>
                                {formatOrderPrice(oldLineTotal)}
                              </span>
                              <span className={styles.desktopDiscountBadge}>
                                -{discountPercent}%
                              </span>
                            </div>
                            <strong className={styles.desktopDiscountPrice}>
                              {formatOrderPrice(lineTotal)}
                            </strong>
                          </>
                        ) : (
                          <strong className={styles.desktopCurrentPrice}>
                            {formatOrderPrice(lineTotal)}
                          </strong>
                        )}
                      </div>

                      <div
                        className={styles.desktopQuantityControls}
                        aria-label={`Кількість: ${item.quantity}`}
                      >
                        <button
                          type="button"
                          disabled
                          aria-label="Зменшити кількість"
                        >
                          <FaMinus aria-hidden="true" />
                        </button>
                        <span>{item.quantity}</span>
                        <button
                          type="button"
                          disabled
                          aria-label="Збільшити кількість"
                        >
                          <FaPlus aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>

        {/* === ПРАВА КОЛОНКА === */}
        <div className={styles.infoSection} ref={infoSectionRef}>
          <div className={styles.infoBlock}>
            <h3>
              <FaUser /> Контактна інформація
            </h3>
            <div className={styles.infoRow}>
              <span>Клієнт:</span>
              <strong>{customerDetails.name || "Н/Д"}</strong>
            </div>
            <div className={styles.infoRow}>
              <span>Телефон:</span>
              <strong>{customerDetails.phone || "Н/Д"}</strong>
            </div>
            <div className={styles.infoRow}>
              <span>Email:</span>
              <strong>{customerDetails.email || "—"}</strong>
            </div>
          </div>

          <div className={styles.infoBlock}>
            <h3>
              <FaTruck /> Доставка
            </h3>

            {/* === Спільні поля === */}
            <div className={styles.infoRow}>
              <span>Спосіб:</span>
              <strong>
                {getDeliveryMethodLabel(customerDetails.deliveryMethod)}
              </strong>
            </div>

            <div className={styles.infoRow}>
              <span>Місто:</span>
              <strong>
                {formatNovaPostCityLabel(
                  customerDetails.novaPoshtaDelivery?.city,
                  customerDetails.city
                ) || "Н/Д"}
              </strong>
            </div>

            {/* === Самовивіз з відділення === */}
            {isBranchDelivery(customerDetails.deliveryMethod) && (
              <>
                <div className={styles.infoRow}>
                  <span>Відділення:</span>
                  <strong>
                    {formatNovaPostDivisionLabel(
                      customerDetails.novaPoshtaDelivery?.division,
                      customerDetails.branchNumber
                    )}
                  </strong>
                </div>
              </>
            )}

            {/* === Кур'єр "Нової пошти" === */}
            {isNovaPoshtaCourierDelivery(customerDetails.deliveryMethod) && (
              <>
                <div className={styles.infoRow}>
                  <span>Адреса:</span>
                  <strong>{formatDeliveryAddress()}</strong>
                </div>
                {customerDetails.courierNote && (
                  <div className={styles.infoRow}>
                    <span>Коментар:</span>
                    <strong>{customerDetails.courierNote}</strong>
                  </div>
                )}
              </>
            )}

            {/* === Кур'єр у межах Києва === */}
            {isKyivCourierDelivery(customerDetails.deliveryMethod) && (
              <>
                <div className={styles.infoRow}>
                  <span>Адреса:</span>
                  <strong>{formatDeliveryAddress()}</strong>
                </div>
                {customerDetails.courierNote && (
                  <div className={styles.infoRow}>
                    <span>Примітка:</span>
                    <strong>{customerDetails.courierNote}</strong>
                  </div>
                )}
              </>
            )}
          </div>

          <div className={styles.infoBlock}>
            <h3>
              <FaMoneyBillWave /> Оплата
            </h3>
            <div className={styles.infoRow}>
              <span>Спосіб:</span>
              <strong>{customerDetails.paymentMethod}</strong>
            </div>
          </div>

          <div className={styles.summaryBox}>
            <h3>
              <FaCheckCircle /> Підсумок
            </h3>
            <p className={styles.summaryText}>
              {cartItems.length} товар(и) у замовленні
            </p>
            <div className={styles.infoRow}>
              <span>Товари:</span>
              <strong>{productsTotalText}</strong>
            </div>
            {hasAuthoritativeTotals &&
              loyaltyQuote.productDiscountKopecks !== null &&
              loyaltyQuote.productDiscountKopecks > 0 && (
                <div className={styles.infoRow}>
                  <span>Знижки на товари:</span>
                  <strong className={styles.discountAmount}>
                    −{formatKopecks(loyaltyQuote.productDiscountKopecks)}
                  </strong>
                </div>
              )}
            {(programSettings || loyaltyQuote?.settings)?.mode === "maintenance" && <p role="status">{LOYALTY_MAINTENANCE_MESSAGE}</p>}
            {hasAuthoritativeTotals && loyaltyQuote.appliedBonusKopecks > 0 && (
              <div className={styles.infoRow}>
                <span>Використані бонуси:</span>
                <strong className={styles.discountAmount}>
                  −{formatKopecks(loyaltyQuote.bonusDiscountKopecks)}
                </strong>
              </div>
            )}
            <div className={styles.infoRow}>
              <span>Доставка:</span>
              <strong>{getDeliveryCost()}</strong>
            </div>
            <hr className={styles.divider} />
            <div className={styles.totalRow}>
              <span>До оплати:</span>
              <strong>{payableItemsText}</strong>
            </div>
            {customer &&
              hasAuthoritativeTotals &&
              (programSettings || loyaltyQuote.settings)?.earningEnabled && loyaltyQuote.estimatedEarnBonusKopecks > 0 && (
                <p className={styles.earnEstimate}>
                  За це замовлення ви отримаєте 
                   {formatKopecks(loyaltyQuote.estimatedEarnBonusKopecks)} на бонусний рахунок.
                </p>
              )}

            <div className={styles.buttons}>
              {usesOnlineCardPayment ? (
                <>
                  <p className={styles.paymentChoiceTitle}>
                    Оберіть зручний спосіб оплати
                  </p>
                  <GooglePayButton
                    disabled={isLoading}
                    testTotalAmount={payableItemsAmount}
                    preparePayment={prepareGooglePayment}
                    submitPayment={payWithGooglePay}
                    onCompleted={completeGooglePayment}
                    onError={showOnlinePaymentError}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      handleOnlinePayment(PAYMENT_CHANNELS.MONOBANK_INVOICE)
                    }
                    className={styles.confirmButton}
                    disabled={isLoading}
                  >
                    <FaCheckCircle /> Оплатити он-лайн через платіжний сервіс
                  </button>
                </>
              ) : (
                <button
                  onClick={handleConfirmOrder}
                  className={styles.confirmButton}
                  disabled={isLoading}
                >
                  <FaCheckCircle /> Підтвердити замовлення
                </button>
              )}
              <button
                onClick={() => navigate("/cart")}
                className={styles.backButton}
                disabled={isLoading}
              >
                <FaArrowLeft /> Повернутись до кошика
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
